import { useEffect, useState } from 'react';
import clsx from 'clsx';

import { useResolvedTheme } from '../theme';
import Modal, { Button, Field, Segmented, inputClass } from './Modal';
import RhythmEditor from './RhythmEditor';
import { usePlanSettingsAction, useRhythmActions, useRhythms } from './data';
import type { PlanSettings, Rhythm, RhythmStatus } from './types';
import { cadenceLabel, hm, minutesLabel, tint } from './util';

type Tab = 'rhythms' | 'sleep';

interface Props {
  settings: PlanSettings;
  statuses: RhythmStatus[];
  initialTab?: Tab;
  onClose: () => void;
}

export default function RhythmsModal({ settings, statuses, initialTab = 'rhythms', onClose }: Props) {
  const [tab, setTab] = useState<Tab>(initialTab);
  const [editing, setEditing] = useState<Rhythm | 'new' | null>(null);
  const rhythmsQ = useRhythms();

  if (editing) {
    return (
      <RhythmEditor
        rhythm={editing === 'new' ? undefined : editing}
        status={editing === 'new' ? undefined : statuses.find((s) => s.rhythm_id === editing.id)}
        onBack={() => setEditing(null)}
        onClose={onClose}
      />
    );
  }

  return (
    <Modal
      title="Rhythms"
      onClose={onClose}
      width={560}
      footer={
        tab === 'rhythms' ? (
          <>
            <span className="flex-1" />
            <Button variant="primary" onClick={() => setEditing('new')}>
              New rhythm
            </Button>
          </>
        ) : undefined
      }
    >
      <div className="mb-4">
        <Segmented<Tab>
          value={tab}
          onChange={setTab}
          options={[
            { value: 'rhythms', label: 'Rhythms' },
            { value: 'sleep', label: 'Sleep and pace' }
          ]}
        />
      </div>
      {tab === 'rhythms' ? (
        <RhythmList rhythms={rhythmsQ.data ?? []} onEdit={setEditing} />
      ) : (
        <SleepAndPace settings={settings} />
      )}
    </Modal>
  );
}

function RhythmList({ rhythms, onEdit }: { rhythms: Rhythm[]; onEdit: (r: Rhythm) => void }) {
  const theme = useResolvedTheme();
  const { reorder, update } = useRhythmActions();
  const groups: { title: string; items: Rhythm[] }[] = [
    { title: 'Sessions', items: rhythms.filter((r) => r.kind === 'count') },
    { title: 'Hours', items: rhythms.filter((r) => r.kind === 'hours') }
  ];

  function move(r: Rhythm, dir: -1 | 1) {
    const ids = rhythms.map((x) => x.id);
    const i = ids.indexOf(r.id);
    // Step past rhythms of the other kind so the move is visible in its group.
    let j = i + dir;
    while (j >= 0 && j < ids.length && rhythms[j].kind !== r.kind) j += dir;
    if (j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    reorder.mutate(ids);
  }

  return (
    <div className="flex flex-col gap-5">
      {groups.map((g) =>
        g.items.length === 0 ? null : (
          <section key={g.title}>
            <h3 className="mb-1 text-[0.72rem] font-medium uppercase tracking-wide text-[var(--text-faint)]">{g.title}</h3>
            <div className="flex flex-col">
              {g.items.map((r, idx) => {
                const t = tint(r.color, theme);
                return (
                  <div
                    key={r.id}
                    className={clsx(
                      'group flex items-center gap-3 rounded-md px-2 py-[7px] hover:bg-[var(--bg-hover)]',
                      !r.active && 'opacity-50'
                    )}
                  >
                    <span className="h-[10px] w-[10px] flex-shrink-0 rounded-[3px]" style={{ background: t.solid }} />
                    <button type="button" onClick={() => onEdit(r)} className="min-w-0 flex-1 text-left">
                      <span className="block truncate text-[0.9rem]">{r.name}</span>
                    </button>
                    <span className="text-[0.78rem] text-[var(--text-muted)]">{cadenceLabel(r)}</span>
                    <span className="flex opacity-0 transition-opacity group-hover:opacity-100">
                      <IconButton label="move up" disabled={idx === 0} onClick={() => move(r, -1)}>
                        ↑
                      </IconButton>
                      <IconButton label="move down" disabled={idx === g.items.length - 1} onClick={() => move(r, 1)}>
                        ↓
                      </IconButton>
                      <IconButton
                        label={r.active ? 'pause' : 'resume'}
                        onClick={() => update.mutate({ id: r.id, active: !r.active })}
                      >
                        {r.active ? '❚❚' : '▶'}
                      </IconButton>
                    </span>
                  </div>
                );
              })}
            </div>
          </section>
        )
      )}
      {rhythms.length === 0 && <p className="text-[0.88rem] text-[var(--text-muted)]">No rhythms yet.</p>}
    </div>
  );
}

function IconButton({
  label,
  onClick,
  disabled,
  children
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label[0].toUpperCase() + label.slice(1)}
      disabled={disabled}
      onClick={onClick}
      className="grid h-7 w-7 place-items-center rounded text-[0.72rem] text-[var(--text-muted)] hover:bg-[var(--bg)] hover:text-[var(--text)] disabled:opacity-30"
    >
      {children}
    </button>
  );
}

function SleepAndPace({ settings }: { settings: PlanSettings }) {
  const save = usePlanSettingsAction();
  const [wake, setWake] = useState(settings.wake_time);
  const [bed, setBed] = useState(settings.bed_time);
  useEffect(() => {
    setWake(settings.wake_time);
    setBed(settings.bed_time);
  }, [settings.wake_time, settings.bed_time]);
  const sleep = (hm(wake) - hm(bed) + 24 * 60) % (24 * 60);

  return (
    <div className="flex flex-col gap-6">
      <section className="flex flex-col gap-3">
        <div className="flex items-end gap-3">
          <Field label="Bed">
            <input
              type="time"
              step={900}
              value={bed}
              onChange={(e) => setBed(e.target.value)}
              onBlur={() => bed !== settings.bed_time && save.mutate({ bed_time: bed })}
              className={clsx(inputClass, 'w-[120px]')}
            />
          </Field>
          <Field label="Wake">
            <input
              type="time"
              step={900}
              value={wake}
              onChange={(e) => setWake(e.target.value)}
              onBlur={() => wake !== settings.wake_time && save.mutate({ wake_time: wake })}
              className={clsx(inputClass, 'w-[120px]')}
            />
          </Field>
          <span className="pb-[7px] text-[0.88rem] tabular-nums text-[var(--text-muted)]">{minutesLabel(sleep)} of sleep</span>
        </div>
      </section>
      <section className="flex flex-col gap-3">
        <h3 className="m-0 text-[0.72rem] font-medium uppercase tracking-wide text-[var(--text-faint)]">Pace</h3>
        <Segmented<'build' | 'full'>
          value={settings.pace}
          onChange={(v) => save.mutate({ pace: v })}
          options={[
            { value: 'build', label: 'Build from recent weeks' },
            { value: 'full', label: 'Full targets' }
          ]}
        />
        {settings.pace === 'build' && (
          <p className="m-0 text-[0.8rem] text-[var(--text-muted)]">
            A rhythm that fell short lately is planned at its recent average plus one.
          </p>
        )}
      </section>
    </div>
  );
}
